import React, { useEffect } from 'react';
import { useStore } from '../store';

const Section: React.FC<{ title: string; children: React.ReactNode; id?: string }> = ({ title, children }) => (
  <section style={{ marginBottom: '2.5rem' }}>
    <h2 style={{
      fontFamily: 'var(--font-display)',
      fontSize: '1.05rem',
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--cyan)',
      marginBottom: '0.85rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.6rem',
    }}>
      <span style={{
        display: 'inline-block',
        width: 3,
        height: '1em',
        background: 'var(--cyan)',
        borderRadius: 2,
        boxShadow: '0 0 6px var(--cyan)',
        flexShrink: 0,
      }} />
      {title}
    </h2>
    <div style={{ fontSize: '0.88rem', color: 'var(--silver-mid)', lineHeight: 1.8 }}>
      {children}
    </div>
  </section>
);

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ marginBottom: '0.75rem' }}>{children}</p>
);

const Ul: React.FC<{ items: string[] }> = ({ items }) => (
  <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem' }}>
    {items.map((item, i) => (
      <li key={i} style={{ marginBottom: '0.35rem' }}>{item}</li>
    ))}
  </ul>
);

export const PrivacyPage: React.FC = () => {
  const { lang } = useStore();
  const fr = lang === 'fr';

  useEffect(() => {
    document.title = fr ? 'Politique de confidentialité — Modern Mercenaries' : 'Privacy Policy — Modern Mercenaries';
  }, [fr]);

  // Check for #cookies hash
  useEffect(() => {
    if (window.location.hash === '#cookies') {
      setTimeout(() => {
        document.getElementById('section-cookies')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  return (
    <div className="page-container" style={{ paddingTop: '3rem', maxWidth: 800 }}>
      {/* Language toggle */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
        <a href="?lang=en" style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.1em',
          padding: '0.25rem 0.6rem',
          border: `1px solid ${!fr ? 'var(--cyan)' : 'var(--navy-border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: !fr ? 'var(--cyan)' : 'var(--silver-dim)',
          background: !fr ? 'var(--cyan-deep)' : 'transparent',
          textDecoration: 'none',
        }}>EN</a>
        <a href="?lang=fr" style={{
          fontFamily: 'var(--font-mono)', fontSize: '0.7rem', letterSpacing: '0.1em',
          padding: '0.25rem 0.6rem',
          border: `1px solid ${fr ? 'var(--cyan)' : 'var(--navy-border)'}`,
          borderRadius: 'var(--radius-sm)',
          color: fr ? 'var(--cyan)' : 'var(--silver-dim)',
          background: fr ? 'var(--cyan-deep)' : 'transparent',
          textDecoration: 'none',
        }}>FR</a>
      </div>

      {/* Title */}
      <div style={{ marginBottom: '2.5rem' }}>
        <div style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.62rem',
          letterSpacing: '0.25em',
          textTransform: 'uppercase',
          color: 'var(--cyan-dim)',
          marginBottom: '0.6rem',
        }}>
          MM//01 — {fr ? 'Document légal' : 'Legal document'}
        </div>
        <h1 style={{
          fontFamily: 'var(--font-display)',
          fontSize: '2.2rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: 'var(--white)',
          lineHeight: 1.1,
        }}>
          {fr ? 'Politique de Confidentialité' : 'Privacy Policy'}
        </h1>
        <p style={{
          fontFamily: 'var(--font-mono)',
          fontSize: '0.7rem',
          color: 'var(--silver-dim)',
          marginTop: '0.6rem',
          letterSpacing: '0.06em',
        }}>
          {fr ? 'Dernière mise à jour : Janvier 2025' : 'Last updated: January 2025'}
        </p>
      </div>

      {/* Glass container */}
      <div style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        borderRadius: 'var(--radius-xl)',
        backdropFilter: 'var(--glass-blur)',
        padding: 'clamp(1.5rem, 4vw, 2.5rem)',
      }}>
        {fr ? (
          <>
            <Section title="1. Qui sommes-nous ?">
              <P>Modern Mercenaries (« nous », « notre ») est une plateforme de mise en relation entre clients et professionnels indépendants. Cette politique explique comment nous traitons les données lorsque vous utilisez notre site.</P>
            </Section>

            <Section title="2. Données que nous collectons">
              <P>Nous collectons uniquement les données nécessaires au fonctionnement du service :</P>
              <Ul items={[
                'Informations de compte : prénom, nom, adresse e-mail, mot de passe (haché), ville, pays.',
                'Informations de profil : biographie, compétences, photo de profil (facultative).',
                'Données de mission : titres, descriptions, compétences requises, budget.',
                'Messages échangés entre utilisateurs via la messagerie interne.',
                'Données de paiement : traitées par notre prestataire de paiement (Stripe) — nous ne stockons pas vos numéros de carte.',
              ]} />
            </Section>

            <Section title="3. Ce que nous NE faisons PAS">
              <P>Nous ne collectons pas votre localisation GPS. Nous ne vendons pas vos données à des tiers. Nous ne partageons pas vos informations personnelles avec des partenaires publicitaires. Nous ne créons pas de profils psychologiques ou comportementaux.</P>
            </Section>

            <Section title="4. Finalités du traitement">
              <Ul items={[
                'Fourniture et amélioration du service.',
                'Communication entre utilisateurs.',
                'Prévention de la fraude et modération.',
                'Traitement des paiements.',
                'Analyse d\'audience (via Google Analytics — voir section Cookies).',
              ]} />
            </Section>

            <Section title="5. Base légale (RGPD)">
              <P>Le traitement de vos données repose sur :</P>
              <Ul items={[
                'L\'exécution du contrat de service (art. 6.1.b RGPD) pour votre compte et vos missions.',
                'Votre consentement (art. 6.1.a RGPD) pour les cookies analytiques et de remarketing.',
                'Notre intérêt légitime (art. 6.1.f RGPD) pour la sécurité et la prévention de fraude.',
              ]} />
            </Section>

            <Section title="6. Durée de conservation">
              <Ul items={[
                'Données de compte : durée d\'activité + 3 ans après la dernière connexion.',
                'Messages : 2 ans.',
                'Données de paiement : 10 ans (obligations légales).',
                'Cookies analytiques : 26 mois (Google Analytics).',
              ]} />
            </Section>

            <Section title="7. Cookies" id="section-cookies">
              <div id="section-cookies" />
              <P>Nous utilisons les cookies suivants :</P>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      {['Nom', 'Fournisseur', 'Finalité', 'Durée'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--cyan)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['_ga, _gid', 'Google Analytics', 'Mesure d\'audience, trafic', '2 ans / 24h'],
                      ['_gcl_au', 'Google Ads', 'Remarketing, conversions', '90 jours'],
                      ['mm_cookie_consent', 'Modern Mercenaries', 'Mémoriser votre choix cookie', '1 an'],
                    ].map(([name, provider, purpose, duration], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(79,195,247,0.07)' }}>
                        {[name, provider, purpose, duration].map((cell, j) => (
                          <td key={j} style={{ padding: '0.5rem 0.75rem', color: 'var(--silver-mid)', fontFamily: j === 0 ? 'var(--font-mono)' : 'inherit', fontSize: j === 0 ? '0.72rem' : '0.82rem' }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>Vous pouvez retirer votre consentement à tout moment via le lien « Gestion des cookies » dans le bas de page, ou en effaçant les cookies de votre navigateur.</P>
            </Section>

            <Section title="8. Vos droits">
              <P>Conformément au RGPD, vous disposez des droits suivants :</P>
              <Ul items={[
                'Droit d\'accès à vos données.',
                'Droit de rectification.',
                'Droit à l\'effacement (« droit à l\'oubli »).',
                'Droit à la limitation du traitement.',
                'Droit à la portabilité.',
                'Droit d\'opposition au traitement.',
                'Droit de retirer votre consentement à tout moment.',
              ]} />
              <P>Pour exercer ces droits, contactez-nous à : <strong style={{ color: 'var(--cyan)' }}>privacy@modernmercenaries.io</strong></P>
            </Section>

            <Section title="9. Sécurité">
              <P>Vos données sont stockées sur des serveurs sécurisés (VPS Hostinger, Debian 12, accès restreint). Les mots de passe sont hachés (bcrypt). Les communications sont chiffrées via HTTPS/TLS. L\'accès à la base de données est limité au réseau interne.</P>
            </Section>

            <Section title="10. Contact">
              <P>Pour toute question relative à cette politique : <strong style={{ color: 'var(--cyan)' }}>privacy@modernmercenaries.io</strong></P>
              <P>Vous pouvez également déposer une réclamation auprès de la CNIL (Commission Nationale de l'Informatique et des Libertés) : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>www.cnil.fr</a></P>
            </Section>
          </>
        ) : (
          <>
            <Section title="1. Who We Are">
              <P>Modern Mercenaries ("we", "our", "us") is a platform connecting clients with independent professionals. This policy explains how we handle data when you use our site.</P>
            </Section>

            <Section title="2. Data We Collect">
              <P>We collect only data necessary to provide the service:</P>
              <Ul items={[
                'Account info: first name, last name, email, hashed password, city, country.',
                'Profile info: bio, skills, profile photo (optional).',
                'Mission data: titles, descriptions, required skills, budget.',
                'Messages exchanged between users via the internal messaging system.',
                'Payment data: processed by Stripe — we do not store card numbers.',
              ]} />
            </Section>

            <Section title="3. What We DO NOT Do">
              <P>We do not collect your GPS location. We do not sell your data to third parties. We do not share your personal information with advertising partners. We do not build psychological or behavioral profiles.</P>
            </Section>

            <Section title="4. Purposes of Processing">
              <Ul items={[
                'Providing and improving the service.',
                'Communication between users.',
                'Fraud prevention and moderation.',
                'Payment processing.',
                'Audience analytics (via Google Analytics — see Cookies section).',
              ]} />
            </Section>

            <Section title="5. Legal Basis (GDPR)">
              <P>Processing of your data is based on:</P>
              <Ul items={[
                'Contract performance (Art. 6.1.b GDPR) for your account and missions.',
                'Your consent (Art. 6.1.a GDPR) for analytics and remarketing cookies.',
                'Legitimate interest (Art. 6.1.f GDPR) for security and fraud prevention.',
              ]} />
            </Section>

            <Section title="6. Retention Periods">
              <Ul items={[
                'Account data: active period + 3 years after last login.',
                'Messages: 2 years.',
                'Payment data: 10 years (legal obligations).',
                'Analytics cookies: 26 months (Google Analytics).',
              ]} />
            </Section>

            <Section title="7. Cookies" id="section-cookies">
              <div id="section-cookies" />
              <P>We use the following cookies:</P>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                      {['Name', 'Provider', 'Purpose', 'Duration'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--cyan)', fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['_ga, _gid', 'Google Analytics', 'Audience measurement, traffic', '2 years / 24h'],
                      ['_gcl_au', 'Google Ads', 'Remarketing, conversions', '90 days'],
                      ['mm_cookie_consent', 'Modern Mercenaries', 'Remember your cookie choice', '1 year'],
                    ].map(([name, provider, purpose, duration], i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(79,195,247,0.07)' }}>
                        {[name, provider, purpose, duration].map((cell, j) => (
                          <td key={j} style={{ padding: '0.5rem 0.75rem', color: 'var(--silver-mid)', fontFamily: j === 0 ? 'var(--font-mono)' : 'inherit', fontSize: j === 0 ? '0.72rem' : '0.82rem' }}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <P>You can withdraw your consent at any time via the "Cookie Settings" link in the footer, or by clearing cookies in your browser settings.</P>
            </Section>

            <Section title="8. Your Rights">
              <P>Under GDPR, you have the following rights:</P>
              <Ul items={[
                'Right of access to your data.',
                'Right to rectification.',
                'Right to erasure ("right to be forgotten").',
                'Right to restriction of processing.',
                'Right to data portability.',
                'Right to object to processing.',
                'Right to withdraw consent at any time.',
              ]} />
              <P>To exercise these rights, contact us at: <strong style={{ color: 'var(--cyan)' }}>privacy@modernmercenaries.io</strong></P>
            </Section>

            <Section title="9. Security">
              <P>Your data is stored on secure servers (Hostinger VPS, Debian 12, restricted access). Passwords are hashed (bcrypt). All communications are encrypted via HTTPS/TLS. Database access is restricted to the internal network.</P>
            </Section>

            <Section title="10. Contact">
              <P>For any questions about this policy: <strong style={{ color: 'var(--cyan)' }}>privacy@modernmercenaries.io</strong></P>
              <P>You may also lodge a complaint with your local data protection authority. In the EU, find your authority at: <a href="https://edpb.europa.eu" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cyan)' }}>edpb.europa.eu</a></P>
            </Section>
          </>
        )}
      </div>
    </div>
  );
};
